import { StorageService } from '../../../../core/data-access/storages/session-local-storages/session-local-storage.service';
import { IToken } from '../dtos/responses/token.response';
import { Token } from '../models/token.model';
import { IUserStorage, UserStorage as UserStorage } from '../models/user-storage.model';
import { IUserContext, UserContext } from '../models/user-context.model';
import { computed, signal } from '@angular/core';

const USER_STORAGE_KEY = 'MAKANI_USER_STORAGE';
const TOKEN_KEY = 'MAKANI_TOKEN';

export class UserContextService {
  static UserStorage = signal<UserStorage | undefined>(UserContextService.get());
  static UserContext = computed(() => UserContextService.UserStorage()?.userContext);
  static hasPermissionFun = computed(() => UserContextService.hasPermission);

  static setToken(token: IToken, isPersist: boolean = false): void {
    token.isPersisted = isPersist;
    StorageService.setItem(TOKEN_KEY, token, isPersist);

    // If there's an existing user storage, update it with the new token
    const currentStorage = UserContextService.UserStorage();
    if (!currentStorage) {
      return;
    }

    const updatedStorage: IUserStorage = {
      token,
      userContext: currentStorage.userContext
    };

    UserContextService.UserStorage.set(UserStorage.create(updatedStorage));
    StorageService.setItem(USER_STORAGE_KEY, updatedStorage, isPersist);
  }

  static setUserStorage(userContext: IUserContext): void {
    let token = this.getToken();

    if (!token) {
      return;
    }

    let userStorage: IUserStorage = {
      token,
      userContext: UserContext.create(userContext)
    };

    UserContextService.UserStorage.set(UserStorage.create(userStorage));
    StorageService.setItem(USER_STORAGE_KEY, userStorage, token.isPersisted);
  }

  static get(): UserStorage | undefined {
    let storage = StorageService.getItem<IUserStorage>(USER_STORAGE_KEY);
    return !storage ? undefined : UserStorage.create(storage);
  }

  static getToken(): Token | undefined {
    let token = StorageService.getItem<IToken>(TOKEN_KEY);
    return token ? Token.create(token) : UserContextService.UserStorage()?.token;
  }

  static getUserContext(): UserContext | undefined {
    return UserContextService.UserContext() || UserContextService.get()?.userContext;
  }

  static isAuthenticated(): boolean {
    const token = UserContextService.getToken();
    return !!token && !token.isExpired();
  }

  static remove(): void {
    UserContextService.UserStorage.set(undefined);
    StorageService.removeItem(USER_STORAGE_KEY);
    StorageService.removeItem(TOKEN_KEY);
  }

  static hasPermission(permissionName: string): boolean {
    let user = UserContextService.getUserContext();

    if (!user) {
      return false;
    }

    return user.hasPermission(permissionName);
  }

  static belongsToSystemAccount(): boolean {
    let user = UserContextService.getUserContext();

    if (!user) {
      return false;
    }

    return user.belongsToSystemAccount();
  }

  static hasSystemRole(systemRoleName: string): boolean {
    let user = UserContextService.getUserContext();

    if (!user) {
      return false;
    }

    return user.hasSystemRole(systemRoleName);
  }

  static updateEmail(newEmail: string): void {
    const currentStorage = UserContextService.UserStorage();
    if (!currentStorage) return;

    const updatedUserContext = { ...currentStorage.userContext, email: newEmail };
    const updatedStorage: UserStorage = {
      ...currentStorage,
      userContext: UserContext.create(updatedUserContext)
    };

    UserContextService.UserStorage.set(updatedStorage);
    StorageService.setItem(USER_STORAGE_KEY, updatedStorage, currentStorage.token.isPersisted);
  }
}
